import { message } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import InfoIcon from "../../../assets/icons/info.svg";
import CopyIcon from "../../../assets/icons/purple-copy.svg";
import ShareIcon from "../../../assets/icons/share.svg";
import PauseIcon from "../../../assets/icons/track-link-pause.svg";
import PlayIcon from "../../../assets/icons/track-link-play.svg";
import TrashIcon from "../../../assets/icons/track-link-trash.svg";
import { TrackingLinkDetailsDto } from "../../../helpers/types";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import { useAppDispatch } from "../../../redux/hooks";
import { clearTrackingLinksCache } from "../../../redux/trackingLinks/slices/trackingLinkSlice";
import { fetchTrackingLinks } from "../../../redux/trackingLinks/thunks/trackingLinksThunks";
import { trackingLinksService } from "../../../services/tracking-links.service";
import { copyToClipboard } from "../../../utls/FunctionsUtil";
import AppHeader from "../../AppHeader/AppHeader";
import DateRangePickerModal from "../../DateRangePickerModalComponent/DateRangePickerModalComponent";
import Loader from "../../loader/Loader";
import ShareModalComponent from "../../ShareModalComponent";
import StickyButtonSmallComponent from "../../StickyButtonSmallComponent";
import ConfirmDeleteLinkModal from "../ConfirmDeleteLinkModal/ConfirmDeleteLinkModal";
import InfoLinkModal from "../InfoLinkModal/InfoLinkModal";
import PausePlayLinkModal from "../PausePlayLinkModal/PausePlayLinkModal";
import "./TrackLinkDetailsView.less";

dayjs.extend(isBetween);

const TrackingLinkDetailsView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser: currentCreator } = useFetchUserInfos();

  const { id } = useParams<{ id: string }>();
  const [trackingLink, setTrackingLink] = useState<TrackingLinkDetailsDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dayjs().subtract(29, "day").toDate(),
    endDate: dayjs().toDate(),
  });

  const items = useMemo(() => {
    if (!trackingLink) return [];

    const start = dayjs(selectedDate.startDate);
    const end = dayjs(selectedDate.endDate);
    const daysCount = end.diff(start, "day") + 1;

    const dataMap = new Map<string, number>();

    for (let i = 0; i < daysCount; i++) {
      const currentDate = start.add(i, "day");
      dataMap.set(currentDate.format("YYYY-MM-DD"), 0);
    }

    if (trackingLink?.clicks?.length) {
      trackingLink.clicks.forEach((click) => {
        const clickDate = dayjs(click.clicksDate);
        if (clickDate.isBetween(start, end, "day", "[]")) {
          const dateKey = clickDate.format("YYYY-MM-DD");
          dataMap.set(dateKey, click.numberOfClicks);
        }
      });
    }

    return Array.from(dataMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, value]) => ({
        label: dayjs(date).format("DD"),
        date: date,
        value,
      }));
  }, [trackingLink, selectedDate]);

  useEffect(() => {
    if (!id) return;

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const res = await trackingLinksService.getTrackingLinkDetails(id!);

        if (mounted) {
          setTrackingLink(res);
        }
      } catch (e) {
        console.error("Failed to fetch tracking link details", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPausePlayModal, setShowPausePlayModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const formatRevenue = (amount: number) => {
    const [integerPart, decimalPart] = amount.toFixed(2).split(".");
    return { integerPart, decimalPart };
  };

  const { integerPart, decimalPart } = formatRevenue(trackingLink?.totalEarnings || 0);

  const handlePauseToggle = () => {
    setShowPausePlayModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await trackingLinksService.deleteTrackingLink(trackingLink?._id!).then((res: any) => {
        if (res) {
          message.success(t("tracking-link-deleted"));
        }
      });

      dispatch(clearTrackingLinksCache());
      if (currentCreator) {
        await (dispatch as any)(fetchTrackingLinks({ creatorId: currentCreator.id }));
      }

      setShowDeleteModal(false);
      navigate(-1);
    } catch (e) {
      console.error(e);
      message.error(t("back-error-message"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPausePlay = async () => {
    try {
      await trackingLinksService.toggleTrackinLinkStatus({ trackLinkId: trackingLink?._id!, isActive: !trackingLink?.isActive }).then((res: any) => {
        if (res) {
          message.success(t("tracking-link-status-changed-successfully"));
        }
      });

      dispatch(clearTrackingLinksCache());
      if (currentCreator) {
        await (dispatch as any)(fetchTrackingLinks({ creatorId: currentCreator.id }));
      }

      navigate("/creator/tracking-links");
    } catch (e) {
      console.error(e);
      message.error(t("back-error-message"));
    } finally {
      setLoading(false);
    }

    setShowPausePlayModal(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleCopyLink = async () => {
    if (!trackingLink) return;

    try {
      await copyToClipboard(trackingLink?.publicLink || "");
      message.success(t("link-copied"));
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  const handleCopyLinkButton = async () => {
    if (!trackingLink) return;
    try {
      await copyToClipboard(trackingLink.link);
      message.success(t("link-copied"));
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  if (!trackingLink) return <></>;

  return (
    <div className="track-link-details-view">
      <AppHeader title={trackingLink?.title} backButtonDestination={-1} showBackButton={true} />

      {loading ? (
        <div className="loader-wrapper">
          <Loader size="large" />
        </div>
      ) : (
        <>
          <div className="track-link-details-content">
            <div className="revenue-section">
              <div className="revenue-amount">
                <span className="revenue-integer">{integerPart},</span>
                <span className="revenue-decimal">{decimalPart}€</span>
              </div>
              <p className="revenue-label">{t("generated-through-this-link")}</p>
            </div>

            <div className="action-buttons">
              <div className="action-button" onClick={handlePauseToggle}>
                <img src={trackingLink?.isActive ? PauseIcon : PlayIcon} alt="pause/play" className="action-icon" />
                <span className="action-text">{trackingLink?.isActive ? t("pause-link") : t("activate-link")}</span>
              </div>

              <div className="action-button" onClick={handleDelete}>
                <img src={TrashIcon} alt="delete" className="action-icon" />
                <span className="action-text">{t("delete-link")}</span>
              </div>

              <div className="action-button" onClick={handleShare}>
                <img src={ShareIcon} alt="share" className="action-icon" />
                <span className="action-text">{t("share-link")}</span>
              </div>
            </div>

            <div className="info-section">
              <h3 className="section-title">{t("statistics")}</h3>
              <div className="info-container">
                <div className="info-row">
                  <span className="info-label">{t("subscription-count")}</span>
                  <span className="info-value">{trackingLink?.totalNumberOfSubscribers}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t("click-count")}</span>
                  <span className="info-value">{trackingLink?.totalNumberOfClicks}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t("total-amount-spent")}</span>
                  <span className="info-value">{trackingLink?.totalEarnings?.toFixed(2).replace(".", ",")}€</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="section-title-with-icon">
                <h3 className="section-title">{t("public-affiliate-link")}</h3>
                <img src={InfoIcon} alt="info" className="info-icon" onClick={handleInfoClick} />
              </div>
              <div className="info-container link-container">
                <span className="affiliate-public-link">{trackingLink?.publicLink}</span>
                <img src={CopyIcon} alt="copy" className="copy-icon" onClick={handleCopyLink} />
              </div>
            </div>

            <div className="info-section chart-section">
              <h3 className="section-title">{t("click-count-title")}</h3>
              <div className="chart-container-wrapper">
                <div className="chart-header">
                  <span className="chart-total-clicks">{items?.reduce((acc, item) => acc + item.value, 0).toLocaleString()}</span>
                  <span className="chart-date-range" onClick={() => setOpenDateRangePicker(true)}>
                    {`${t("from")} ${dayjs(selectedDate?.startDate).format("DD/MM/YYYY")} ${t("to")} ${dayjs(selectedDate?.endDate).format("DD/MM/YYYY")}`}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={items} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="clickGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7FFAA1" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#7FFAA1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide={true} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const formattedDate = dayjs(data.date).format("DD MMMM");
                          const clicks = data.value;
                          return (
                            <div
                              style={{
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                padding: "5px 14px",
                                borderRadius: "8px",
                                color: "#FFFFFF",
                              }}
                            >
                              <p style={{ margin: 0, fontSize: "12px", fontWeight: "bold" }}>
                                {clicks} {clicks > 1 ? "clics" : "clic"} le {formattedDate}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#7FFAA1"
                      fill="url(#clickGradient)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: "#7FFAA1" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <StickyButtonSmallComponent title={t("copy-link-button")} handleClickButton={handleCopyLinkButton} />

          {showDeleteModal && <ConfirmDeleteLinkModal onConfirm={handleConfirmDelete} onCancel={() => setShowDeleteModal(false)} />}

          {showPausePlayModal && (
            <PausePlayLinkModal isActive={trackingLink?.isActive!} onConfirm={handleConfirmPausePlay} onCancel={() => setShowPausePlayModal(false)} />
          )}

          {showInfoModal && <InfoLinkModal onClose={() => setShowInfoModal(false)} />}

          <ShareModalComponent isOpen={showShareModal} onClose={() => setShowShareModal(false)} link={trackingLink?.link} />

          {openDateRangePicker && (
            <DateRangePickerModal
              onClose={() => {
                setOpenDateRangePicker(false);
              }}
              selectedDate={selectedDate}
              onDateSelect={(startDate, endDate) => {
                setSelectedDate({ startDate, endDate });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TrackingLinkDetailsView;
